/**
 * CloudWatch RUM client (EI-4 / #35, §3.4). Initializes the AWS RUM web SDK from
 * runtime config (fetched via `GET /api/config` — never rebuilt-per-env, which is the
 * `client/.env` landmine we already learned from). Absent / failed config → no RUM,
 * silently: `record*` helpers below become no-ops so callers don't have to guard.
 *
 * Auto-captured by the SDK config (see `initRum`): **page-load / web-vitals**
 * (`performance` telemetry) and **JS errors** (`errors` telemetry — unhandled
 * exceptions + rejections are reported without any manual call). Those two exit-criteria
 * signals need no wiring here.
 *
 * Custom events (§3.4):
 *   - `ws_reconnect` — WIRED: fired every time `WsClient` re-establishes its socket
 *     (`ws-client.ts` onopen, when reconnectAttempts > 0).
 *   - `buzz_rtt`     — helper provided, **wiring is a tracked follow-up.** The intended
 *     measurement is a client-observed buzz round-trip for BOTH the WIN path (this
 *     player becomes `clueState.activePlayerId`) and the LOSE path (another player
 *     becomes active, or the clue closes without this player winning): start = the
 *     timestamp when this player's `buzz` is sent; stop = the first `clueState`
 *     transition after that send. This needs the local `playerId` + clueState-outcome
 *     threaded to a UI-layer emitter (the player page), which is deferred to a small
 *     follow-up; `recordBuzzRtt` below is the ready seam. `recordRumError` is likewise
 *     available for any manual error reporting beyond the SDK's auto-capture.
 *
 * The SDK is loaded via dynamic import so the bundle-size cost is paid only when RUM
 * is actually configured (local/dev bundles never load it).
 */

import { FUNNEL_RUM_EVENT_TYPE, type FunnelStage } from '@jeopardy/shared';
import type { CloudWatchRumClientConfig } from './config-api';
import { getFunnelKey } from './funnel-key';
import { getSessionId } from './session-id';

/** Minimal RUM SDK surface we use — matches `aws-rum-web`'s `AwsRum` (v3.2.1). */
interface AwsRumLike {
  recordEvent: (eventType: string, eventData: Record<string, unknown>) => void;
  recordError: (error: unknown) => void;
  /**
   * Attach attributes to EVERY event in the current RUM session — page views, JS errors,
   * http spans, AND custom events — so all RUM data is stamped with the player/user id
   * (aws-rum-web `Orchestration.addSessionAttributes`, v3.2.1). Values are string | boolean
   * | number. This is how identity reaches the auto-captured telemetry we never call by hand.
   */
  addSessionAttributes: (attributes: Record<string, string | boolean | number>) => void;
  /**
   * Adopt an externally-minted session id so RUM's own session id becomes OUR
   * browser-session id (`aws-rum-web` `Orchestration.pinSessionId`, v3.2.1). It does NOT
   * re-roll sampling, so pinning after init is safe. Optional so a build of the SDK
   * without it still type-checks (we guard the call).
   */
  pinSessionId?: (sessionId: string) => void;
}

let rumClient: AwsRumLike | null = null;
let initPromise: Promise<AwsRumLike | null> | null = null;
/**
 * The identity already promoted to RUM session attributes, so repeated calls (page load,
 * sign-in, each funnel event) are idempotent and we only call `addSessionAttributes` when
 * the identity actually changes (e.g. a guest→registered claim).
 */
let lastIdentityKey: string | null = null;

/**
 * Initialize the RUM SDK, memoized. Safe to call multiple times (subsequent calls
 * return the same client). Returns null when the SDK fails to load or throws — RUM
 * MUST NOT crash the page (it's telemetry).
 */
export function initRum(config: CloudWatchRumClientConfig): Promise<AwsRumLike | null> {
  if (initPromise) return initPromise;
  initPromise = (async (): Promise<AwsRumLike | null> => {
    try {
      // Dynamic import with a STATIC string literal so Vite code-splits `aws-rum-web`
      // into its own lazy chunk — fetched only when RUM is configured (this branch), so
      // dev/unconfigured loads never pay the bundle-size cost. `aws-rum-web` is a real
      // `@jeopardy/web` dependency (package.json) so the chunk is actually built and
      // browser-resolvable. (Previously this used `@vite-ignore` + a variable module id,
      // which left a bare `import('aws-rum-web')` specifier in the shipped bundle that the
      // browser could not resolve — the import threw, RUM silently never initialized, and
      // NO RUM requests were ever sent. Regression fixed here.)
      const mod = (await import('aws-rum-web')) as {
        AwsRum: new (id: string, ver: string, region: string, cfg: unknown) => AwsRumLike;
      };
      const client = new mod.AwsRum(config.applicationId, '1.0.0', config.region, {
        sessionSampleRate: config.sessionSampleRate ?? 1.0,
        guestRoleArn: undefined,
        identityPoolId: config.identityPoolId,
        endpoint: `https://dataplane.rum.${config.region}.amazonaws.com`,
        // RUM→server trace stitching (EI-4 / #35). `enableXRay` + `enableW3CTraceId` +
        // the http telemetry's `addXRayTraceIdHeader` make RUM stamp a **W3C `traceparent`**
        // header (NOT `X-Amzn-Trace-Id`) on same-origin API/WS requests. W3C is the ALB-safe
        // path: the ALB owns/rewrites `X-Amzn-Trace-Id`, but leaves `traceparent` alone, and
        // the server extracts it via the composite W3C+X-Ray propagator — so a real-user
        // session and the server spans its requests drive become one connected trace.
        // Same-origin only (CloudFront), so the header never leaks cross-origin.
        // NOTE: this client half only *records* the browser segment + stamps the header.
        // For that segment to actually reach the trace store (X-Ray → Transaction Search →
        // Omni `traces.default`), the RUM **app monitor** must also have `enableXRay: true`
        // (infra: `jeopardy-distributed-stack.ts` `appMonitorConfiguration`) — the browser
        // may only call `rum:PutRumEvents`; RUM's backend does the X-Ray forwarding.
        telemetries: ['performance', 'errors', ['http', { addXRayTraceIdHeader: true }]],
        allowCookies: true,
        enableXRay: true,
        enableW3CTraceId: true,
      });
      // Align RUM's session id with OUR stable browser-session id (#35 session work). We
      // own the id (see `session-id.ts`) so both this browser's RUM data AND the backend
      // spans its requests drive (which receive the same id via the WS `?sid=` param and
      // the `x-jeopardy-session-id` header) key on ONE value. `pinSessionId` makes RUM's
      // native session id equal ours (no re-roll of sampling); the `session_id` session
      // attribute additionally stamps it on every RUM event for explicit queryability.
      try {
        const sid = getSessionId();
        client.pinSessionId?.(sid);
        client.addSessionAttributes({ session_id: sid });
      } catch {
        // Telemetry never affects the page.
      }
      rumClient = client;
      return client;
    } catch (error) {
      console.warn('[rum] init failed — telemetry disabled', error);
      return null;
    }
  })();
  return initPromise;
}

/** Test-only reset. */
export function resetRumForTests(): void {
  rumClient = null;
  initPromise = null;
  lastIdentityKey = null;
}

/**
 * Promote the known player/user identity to RUM **session attributes** (#35), so ALL RUM
 * data for this session — page views, JS errors, http spans, and custom events — carries
 * the identity. This is the client half of the identity-enrichment story: a real user's
 * RUM session (browser side) and their backend spans (server side) both carry `player_id`,
 * and the RUM http spans additionally stamp a W3C `traceparent` (see `initRum`) so they
 * join the server SERVER spans by trace id.
 *
 * `player_id` mirrors the backend `player.id` span attribute (the durable, canonical id —
 * for a registered user this is stable across devices), which is the join key for
 * usage-by-user; the backend maps `player.id → enduser.id` (Cognito sub) server-side, so
 * the browser never needs to hold the sub. `is_guest` distinguishes anonymous from
 * registered sessions.
 *
 * Idempotent + safe no-op before init (memoized until the identity changes). Called once
 * the id is known: on load from the persisted identity, on sign-in, and — generalizing the
 * per-event `player_id` stamp — the first time a funnel event carries a `playerId`.
 *
 * PRIVACY (#35): only the opaque `player_id` + a boolean flag are attached — never a name,
 * email, or token. Consistent with the owner decision that span/RUM content capture is ON.
 */
export function setRumIdentity(identity: { playerId?: string; isGuest?: boolean }): void {
  if (!identity.playerId) return;
  const key = `${identity.playerId}|${identity.isGuest === false ? 'r' : 'g'}`;
  if (key === lastIdentityKey) return;
  try {
    rumClient?.addSessionAttributes({
      player_id: identity.playerId,
      ...(identity.isGuest === undefined ? {} : { is_guest: identity.isGuest }),
    });
    // Only memoize once actually applied (rumClient present) so a pre-init call re-attempts
    // after init rather than latching the key while RUM was still null.
    if (rumClient) lastIdentityKey = key;
  } catch {
    // Telemetry never affects the page.
  }
}

/** Record a JS error (safe no-op when RUM isn't initialized). */
export function recordRumError(error: unknown): void {
  try {
    rumClient?.recordError(error);
  } catch {
    // Swallow — telemetry never affects the page.
  }
}

/**
 * Record a client adoption-funnel stage (#148, NFR-6.6) as a RUM custom event. The event
 * rides the RUM session, so the auto-captured `errors`/`http` telemetry for the same
 * session lets us correlate WHAT error preceded a drop-off (D148.1). Always stamps the
 * anonymous first-party `fkey` (the funnel head, D148.9); `playerId` is included once
 * known. Safe no-op when RUM isn't initialized (local/dev/absent config).
 *
 * Only client-observable UX moments are emitted here (`landed`, `signup_started`,
 * `checkout_started`); the identity/purchase/game CONVERSIONS are emitted
 * server-authoritatively (exact, sampling-independent) and are never trusted from the
 * client.
 */
export function recordFunnelStage(
  stage: FunnelStage,
  details?: { playerId?: string; props?: Record<string, string | number | boolean> },
): void {
  // Generalize the per-event `player_id` stamp into a session-wide attribute (#35): the
  // first funnel event that knows the player promotes the id to ALL RUM data for the
  // session (covers a brand-new guest whose id is minted mid-session, after load).
  if (details?.playerId) setRumIdentity({ playerId: details.playerId });
  try {
    rumClient?.recordEvent(FUNNEL_RUM_EVENT_TYPE, {
      stage,
      fkey: getFunnelKey(),
      ...(details?.playerId ? { player_id: details.playerId } : {}),
      ...(details?.props ?? {}),
      ts: Date.now(),
    });
  } catch {
    // Telemetry never affects the page.
  }
}

/** Record a WS reconnect (safe no-op when RUM isn't initialized). */
export function recordWsReconnect(details?: { attempt?: number; reason?: string }): void {
  try {
    rumClient?.recordEvent('ws_reconnect', {
      attempt: details?.attempt ?? 0,
      ...(details?.reason ? { reason: details.reason } : {}),
      ts: Date.now(),
    });
  } catch {
    // ignore
  }
}

/**
 * Record a client-observed buzz round-trip. `outcome` is `win` when this player got
 * the buzzer, `lose` otherwise (locked out, timeout, or another player active).
 */
export function recordBuzzRtt(details: {
  latencyMs: number;
  outcome: 'win' | 'lose';
  gameId?: string;
}): void {
  if (!Number.isFinite(details.latencyMs) || details.latencyMs < 0) return;
  try {
    rumClient?.recordEvent('buzz_rtt', {
      latency_ms: Math.round(details.latencyMs),
      outcome: details.outcome,
      ...(details.gameId ? { game_id: details.gameId } : {}),
    });
  } catch {
    // ignore
  }
}
